import React, { useState, useEffect, KeyboardEvent, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ApiService } from '../services/api';
import CharacterTag from './common/CharacterTag';
import SuggestionChip from './common/SuggestionChip';
import { useEffectOnce } from '../hooks/useEffectOnce';
import '../styles/StoryForm.css';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';
import { SavedSetting, SavedCharacter } from '../types/workshop';

interface StoryFormProps {
  onSubmit: (formData: StoryFormData) => void;
  isLoading: boolean;
}

export interface StoryFormData {
  title: string;
  mood: string;
  setting: string;
  characters: string[];
  length: string;
  voice: string;
  backgroundNoise?: string;
  additionalDetails: string;
  generateImages: boolean;
}

const StoryForm: React.FC<StoryFormProps> = ({ onSubmit, isLoading }) => {
  // Form state
  const [formData, setFormData] = useState<StoryFormData>({
    title: '',
    mood: 'happy',
    setting: '',
    characters: [],
    length: 'medium',
    voice: 'friendly',
    backgroundNoise: 'brown',
    additionalDetails: '',
    generateImages: false
  });

  // Character input field state
  const [characterInput, setCharacterInput] = useState('');
  const [isCustomCharacter, setIsCustomCharacter] = useState(false);
  
  // Suggestions state
  const [settingSuggestions, setSettingSuggestions] = useState<string[]>([]);
  const [characterSuggestions, setCharacterSuggestions] = useState<string[]>([]);
  
  // Loading state for suggestions
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isLoadingCharacters, setIsLoadingCharacters] = useState(false);
  
  // Custom setting input state
  const [isCustomSetting, setIsCustomSetting] = useState(false);
  
  // Workshop data state
  const [savedSettings, setSavedSettings] = useState<SavedSetting[]>([]);
  const [savedCharacters, setSavedCharacters] = useState<SavedCharacter[]>([]);
  const [isLoadingWorkshopData, setIsLoadingWorkshopData] = useState(false);

  const { getAccessToken } = useAuth();
  
  // Add location and previous route tracking
  const location = useLocation();
  const previousRouteRef = useRef<string | null>(null);
  const dataFetchedRef = useRef<boolean>(false);

  // Function to fetch all data
  const fetchAllData = async () => {
    logger.log('🔄 Fetching initial suggestions and workshop data');
    try {
      // Fetch AI suggestions and workshop data in parallel
      setIsLoadingSettings(true);
      setIsLoadingCharacters(true);
      setIsLoadingWorkshopData(true);
      
      await Promise.all([
        // Fetch AI settings
        (async () => {
          try {
            const suggestions = await ApiService.getSettingSuggestions(5);
            logger.log(`✅ Fetched ${suggestions.length} setting suggestions`);
            setSettingSuggestions(suggestions);
          } catch (error) {
            logger.error('Error fetching setting suggestions:', error);
          } finally {
            setIsLoadingSettings(false);
          }
        })(),
        
        // Fetch AI characters
        (async () => {
          try {
            const suggestions = await ApiService.getCharacterSuggestions(5);
            logger.log(`✅ Fetched ${suggestions.length} character suggestions`);
            setCharacterSuggestions(suggestions);
          } catch (error) {
            logger.error('Error fetching character suggestions:', error);
          } finally {
            setIsLoadingCharacters(false);
          }
        })(),
        
        // Fetch workshop settings
        (async () => {
          try {
            const settings = await ApiService.getSavedSettings();
            logger.log(`✅ Fetched ${settings.length} saved settings`);
            setSavedSettings(settings);
          } catch (error) {
            logger.error('Error fetching saved settings:', error);
          }
        })(),
        
        // Fetch workshop characters
        (async () => {
          try {
            const characters = await ApiService.getSavedCharacters();
            logger.log(`✅ Fetched ${characters.length} saved characters`);
            setSavedCharacters(characters);
          } catch (error) {
            logger.error('Error fetching saved characters:', error);
          }
        })()
      ]);
      
      // Mark data as fetched
      dataFetchedRef.current = true;
    } catch (error) {
      logger.error('Error in fetchAllData:', error);
    } finally {
      setIsLoadingWorkshopData(false);
    }
  };

  // Replace useEffectOnce with navigation-aware useEffect
  useEffect(() => {
    // If we haven't fetched data yet OR we're coming back from a different route
    if (!dataFetchedRef.current || 
        (previousRouteRef.current !== null && 
         previousRouteRef.current !== location.pathname)) {
      fetchAllData();
    }
    
    // Update previous route ref
    previousRouteRef.current = location.pathname;
    
    // Cleanup function
    return () => {
      // Clean up if necessary
    };
  }, [location.pathname]);

  // Mood options
  const moodOptions = [
    'happy', 'exciting', 'calming', 
    'silly', 'educational', 'mysterious'
  ];

  // Handle form field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle setting selection
  const handleSettingSelect = (setting: string) => {
    setFormData(prevData => ({
      ...prevData,
      setting
    }));
    setIsCustomSetting(false);
  };

  // Handle custom setting change
  const handleCustomSettingChange = (value: string) => {
    setFormData(prevData => ({
      ...prevData,
      setting: value
    }));
  };

  // Toggle custom setting input
  const toggleCustomSetting = () => {
    setIsCustomSetting(!isCustomSetting);
    if (!isCustomSetting) {
      setFormData(prev => ({
        ...prev,
        setting: ''
      }));
    }
  };

  // Toggle custom character input
  const toggleCustomCharacter = () => {
    setIsCustomCharacter(!isCustomCharacter);
    if (!isCustomCharacter) {
      setCharacterInput('');
    }
  };

  // Handle character input changes
  const handleCharacterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCharacterInput(e.target.value);
  };

  // Add a character from input when input is complete
  const handleCustomCharacterChange = (value: string) => {
    setCharacterInput(value);
    
    // If user presses Enter or input loses focus with content, add the character
    if (value.trim() && !formData.characters.includes(value.trim())) {
      setFormData(prev => ({
        ...prev,
        characters: [...prev.characters, value.trim()]
      }));
    }
  };

  // Add a character when Enter key is pressed
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (characterInput.trim() && !formData.characters.includes(characterInput.trim())) {
        setFormData(prev => ({
          ...prev,
          characters: [...prev.characters, characterInput.trim()]
        }));
        setCharacterInput('');
      }
    }
  };

  // Add a character from suggestions
  const addCharacterFromSuggestion = (character: string) => {
    if (!formData.characters.includes(character)) {
      setFormData(prev => ({
        ...prev,
        characters: [...prev.characters, character]
      }));
    }
  };

  // Remove a character by index
  const removeCharacter = (index: number) => {
    setFormData(prev => ({
      ...prev,
      characters: prev.characters.filter((_, i) => i !== index)
    }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Create a copy of the form data to enhance
    const enhancedFormData = { ...formData };
    
    // Enhance setting with details if it's a saved setting
    const matchedSetting = savedSettings.find(s => s.setting === formData.setting);
    if (matchedSetting) {
      // If the setting matches a saved setting, enhance it with the description
      enhancedFormData.setting = `${matchedSetting.name}: ${matchedSetting.setting}${matchedSetting.description ? ` (${matchedSetting.description})` : ''}`;
    }
    
    // Enhance characters with details if they're saved characters
    if (formData.characters.length > 0) {
      enhancedFormData.characters = formData.characters.map(charName => {
        const matchedChar = savedCharacters.find(c => c.name === charName);
        if (matchedChar) {
          // If the character matches a saved character, enhance it with the description and details
          return `${matchedChar.name}: ${matchedChar.character}${matchedChar.description ? ` (${matchedChar.description})` : ''}`;
        }
        return charName; // Keep as is if not a saved character
      });
    }
    
    // Submit the form data including any additionalDetails
    onSubmit(enhancedFormData);
  };

  // Handle selecting a workshop setting
  const handleWorkshopSettingSelect = (setting: SavedSetting) => {
    // Store just the setting text, not the full object
    // The details will be added at submission time
    setFormData(prevData => ({
      ...prevData,
      setting: setting.setting
    }));
    setIsCustomSetting(false);
  };

  // Handle selecting a workshop character
  const handleWorkshopCharacterSelect = (character: SavedCharacter) => {
    // Store just the character name, not the full object
    // The details will be added at submission time
    if (!formData.characters.includes(character.name)) {
      setFormData(prevData => ({
        ...prevData,
        characters: [...prevData.characters, character.name]
      }));
    }
  };

  // Refresh only AI settings suggestions
  const refreshSettingSuggestions = async () => {
    try {
      logger.log('🔄 Manually refreshing setting suggestions');
      setIsLoadingSettings(true);
      const suggestions = await ApiService.getSettingSuggestions(5);
      logger.log(`✅ Refreshed ${suggestions.length} setting suggestions`);
      setSettingSuggestions(suggestions);
    } catch (error) {
      logger.error('Error refreshing setting suggestions:', error);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  // Refresh only AI character suggestions
  const refreshCharacterSuggestions = async () => {
    try {
      logger.log('🔄 Manually refreshing character suggestions');
      setIsLoadingCharacters(true);
      const suggestions = await ApiService.getCharacterSuggestions(5);
      logger.log(`✅ Refreshed ${suggestions.length} character suggestions`);
      setCharacterSuggestions(suggestions);
    } catch (error) {
      logger.error('Error refreshing character suggestions:', error);
    } finally {
      setIsLoadingCharacters(false);
    }
  };

  return (
    <div className="story-form-container">
      <h2>Create a Magical Bedtime Story</h2>
      <form onSubmit={handleSubmit} className="story-form">
        {/* Basic Information Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>
              <span className="section-title-text">Basic Information</span>
            </h3>
          </div>
          
          <div className="form-group">
            <label htmlFor="title">Story Title (optional)</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="A title for your story"
              className="full-width"
            />
            <div className="form-hint">Leave blank for an auto-generated title</div>
          </div>
          
          <div className="form-group">
            <label htmlFor="mood">Mood</label>
            <div className="mood-selector">
              {moodOptions.map(mood => (
                <label key={mood} className={`mood-option ${formData.mood === mood ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="mood"
                    value={mood}
                    checked={formData.mood === mood}
                    onChange={handleChange}
                  />
                  <span className="mood-label">{mood.charAt(0).toUpperCase() + mood.slice(1)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Settings Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>
              <span className="section-title-text">Choose a Setting</span>
            </h3>
          </div>
          
          <div className="refresh-button-container">
            <button 
              type="button" 
              className="refresh-button" 
              onClick={refreshSettingSuggestions}
              disabled={isLoadingSettings}
              aria-label="Refresh setting suggestions"
            >
              {isLoadingSettings ? 'Loading...' : 'Refresh Settings'}
            </button>
          </div>

          {isLoadingSettings ? (
            <div className="suggestions-loading">Loading magical places...</div>
          ) : (
            <div className="suggestion-chips">
              {/* AI Suggestions */}
              {settingSuggestions.map((suggestion, index) => (
                <SuggestionChip 
                  key={`ai-setting-${index}`} 
                  label={suggestion} 
                  isSelected={formData.setting === suggestion}
                  onClick={() => handleSettingSelect(suggestion)} 
                />
              ))}
              
              {/* Workshop Settings */}
              {savedSettings.map(setting => (
                <SuggestionChip 
                  key={`workshop-setting-${setting.id}`} 
                  label={setting.name} 
                  isSelected={formData.setting === setting.setting}
                  onClick={() => handleWorkshopSettingSelect(setting)}
                  isWorkshopItem={true}
                  tooltip={setting.setting}
                />
              ))}
              
              <button 
                type="button" 
                className={`custom-option-button ${isCustomSetting ? 'active' : ''}`}
                onClick={toggleCustomSetting}
              >
                Custom Setting
              </button>
            </div>
          )}

          {isCustomSetting && (
            <div className="form-group">
              <input
                type="text"
                placeholder="Describe the setting for your story"
                value={formData.setting}
                onChange={(e) => handleCustomSettingChange(e.target.value)}
                className="full-width"
              />
            </div>
          )}
        </div>

        {/* Characters Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>
              <span className="section-title-text">Add Characters</span>
            </h3>
          </div>
          
          <div className="refresh-button-container">
            <button 
              type="button" 
              className="refresh-button" 
              onClick={refreshCharacterSuggestions}
              disabled={isLoadingCharacters}
              aria-label="Refresh character suggestions"
            >
              {isLoadingCharacters ? 'Loading...' : 'Refresh Characters'}
            </button>
          </div>

          {isLoadingCharacters ? (
            <div className="suggestions-loading">Loading character ideas...</div>
          ) : (
            <div className="suggestion-chips">
              {/* AI Character Suggestions */}
              {characterSuggestions.map((suggestion, index) => (
                <SuggestionChip 
                  key={`ai-character-${index}`}
                  label={suggestion} 
                  isSelected={formData.characters.includes(suggestion)}
                  onClick={() => addCharacterFromSuggestion(suggestion)} 
                />
              ))}
              
              {/* Workshop Characters */}
              {savedCharacters.map(character => (
                <SuggestionChip 
                  key={`workshop-character-${character.id}`}
                  label={character.name} 
                  isSelected={formData.characters.includes(character.name)}
                  onClick={() => handleWorkshopCharacterSelect(character)}
                  isWorkshopItem={true}
                  tooltip={character.character}
                />
              ))}
              
              <button 
                type="button" 
                className={`custom-option-button ${isCustomCharacter ? 'active' : ''}`}
                onClick={toggleCustomCharacter}
              >
                Custom Character
              </button>
            </div>
          )}

          {isCustomCharacter && (
            <div className="form-group">
              <input
                type="text"
                placeholder="Describe a character for your story"
                value={characterInput}
                onChange={handleCharacterInputChange}
                onKeyDown={handleKeyDown}
                className="full-width"
              />
            </div>
          )}

          {formData.characters.length > 0 && (
            <div className="character-tags">
              {formData.characters.map((character, index) => (
                <CharacterTag 
                  key={index} 
                  name={character} 
                  onRemove={() => removeCharacter(index)} 
                />
              ))}
            </div>
          )}

          {formData.characters.length === 0 && (
            <div className="no-characters-message">
              Add at least one character to your story
            </div>
          )}
        </div>

        {/* Additional Details Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>
              <span className="section-title-text">Additional Details</span>
            </h3>
          </div>
          
          <div className="form-group">
            <label htmlFor="additionalDetails">Story Customization (optional)</label>
            <textarea
              id="additionalDetails"
              name="additionalDetails"
              value={formData.additionalDetails}
              onChange={handleChange}
              placeholder="Add any specific elements you'd like to include in your story (e.g., moral lessons, specific events, special objects, or unique story elements)"
              className="full-width"
              rows={4}
            />
            <div className="form-hint">
              Use this to customize your story further with specific elements, themes, or details you'd like the AI to include.
            </div>
          </div>
        </div>

        {/* Story Options Section */}
        <div className="form-section">
          <div className="section-header">
            <h3>
              <span className="section-title-text">Story Options</span>
            </h3>
          </div>
          
          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="length">Story Length</label>
              <select
                id="length"
                name="length"
                value={formData.length}
                onChange={handleChange}
                required
              >
                <option value="short">Short (2-3 minutes)</option>
                <option value="medium">Medium (5-7 minutes)</option>
                <option value="long">Long (10-12 minutes)</option>
              </select>
              <div className="form-hint">
                Affects how detailed and long the generated story will be.
              </div>
            </div>

            <div className="form-group half-width">
              <label htmlFor="voice">Narrator Voice</label>
              <select
                id="voice"
                name="voice"
                value={formData.voice}
                onChange={handleChange}
                required
              >
                <option value="friendly">Friendly and Warm</option>
                <option value="excited">Excited and Energetic</option>
                <option value="calm">Calm and Soothing</option>
                <option value="mysterious">Mysterious and Whispery</option>
              </select>
              <div className="form-hint">
                Affects the tone and style of the story narration.
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="backgroundNoise">Background Sound</label>
            <select
              id="backgroundNoise"
              name="backgroundNoise"
              value={formData.backgroundNoise}
              onChange={handleChange}
            >
              <option value="none">None</option>
              <option value="brown">Brown Noise</option>
            </select>
            <div className="form-hint">
              Brown noise plays softly during narration to help create a soothing environment.
            </div>
          </div>

          <div className="form-group">
            <div className="toggle-control">
              <label htmlFor="generateImages">Generate Images</label>
              <div className="toggle-switch-wrapper">
                <input
                  type="checkbox"
                  id="generateImages"
                  name="generateImages"
                  checked={formData.generateImages}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      generateImages: e.target.checked
                    }));
                  }}
                  className="toggle-switch"
                />
                <label htmlFor="generateImages" className="toggle-label">
                  <span className="toggle-inner"></span>
                  <span className="toggle-switch"></span>
                </label>
              </div>
            </div>
            <div className="form-hint">
              AI will create illustrations based on your story. Images will appear throughout your story.
              {formData.length === 'short' && ' (2-3 images)'}
              {formData.length === 'medium' && ' (3-4 images)'}
              {formData.length === 'long' && ' (4-5 images)'}
            </div>
          </div>
        </div>

        <button 
          type="submit" 
          className="submit-button"
          disabled={isLoading || !formData.setting || formData.characters.length === 0}
        >
          {isLoading ? 'Creating Your Story...' : 'Generate Bedtime Story'} 
        </button>
      </form>
    </div>
  );
};

export default StoryForm; 